export type ManagementTableSectionId =
  | 'locations'
  | 'schedule-types'
  | 'service-lines'
  | 'groups-teams'
  | 'scheduler-admins';

export type ManagementTableRow = {
  id: string;
  status: string;
  sortOrder: number;
  lastUpdated: string;
  dependencyWarning: string | null;
  cells: Record<string, string>;
};

export type ManagementTableStore = Record<ManagementTableSectionId, ManagementTableRow[]>;

export type ImportFieldStatus = 'valid' | 'warning' | 'error';

export type ImportCellValidation = {
  status: ImportFieldStatus;
  messages: string[];
};

export type ImportFieldDefinition = {
  key: string;
  label: string;
  aliases: string[];
  required?: boolean;
  templateOnly?: boolean;
  templateDescription?: string;
  defaultValue?: string;
  inputPlaceholder?: string;
};

export type ImportSectionDefinition = {
  title: string;
  primaryFieldKey: string;
  defaultStatus: string;
  acceptedFormats: string[];
  generalRules: string[];
  fields: ImportFieldDefinition[];
  templateRows: Array<Record<string, string>>;
  samplePaste: string;
  followUpNote: string;
};

export type ParsedImportBatch = {
  sourceMode: 'headered' | 'headerless' | 'simple-list';
  delimiter: 'tab' | 'csv' | 'newline';
  columnLabels: string[];
  records: string[][];
  defaultMappings: string[];
  summary: string;
};

export type ImportPreviewRow = {
  rowIndex: number;
  status: ImportFieldStatus;
  messages: string[];
  cells: Record<string, string>;
  cellValidation: Record<string, ImportCellValidation>;
};

export type ImportPreview = {
  rows: ImportPreviewRow[];
  counts: Record<ImportFieldStatus, number>;
  committableCount: number;
  mappingWarnings: string[];
};

export const IMPORT_IGNORE_FIELD = '__ignore__';

const splitMultiValueCell = (value: string) =>
  value
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isTruthyValue = (value: string) => {
  const normalized = normalizeKey(value);
  return normalized === 'yes'
    || normalized === 'y'
    || normalized === 'true'
    || normalized === '1'
    || normalized === 'companywide'
    || normalized === 'company wide'
    || normalized === 'all locations'
    || normalized === 'all';
};

const expandHexColor = (value: string) => {
  const normalized = value.trim();

  if (!colorHexRegex.test(normalized)) {
    return null;
  }

  if (normalized.length === 4) {
    const [, red, green, blue] = normalized;
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }

  return normalized.toUpperCase();
};

const getChannelLuminance = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const getContrastRatio = (leftHex: string, rightHex: string) => {
  const normalizedLeft = expandHexColor(leftHex);
  const normalizedRight = expandHexColor(rightHex);

  if (!normalizedLeft || !normalizedRight) {
    return 1;
  }

  const toRgb = (hex: string) => ({
    red: Number.parseInt(hex.slice(1, 3), 16),
    green: Number.parseInt(hex.slice(3, 5), 16),
    blue: Number.parseInt(hex.slice(5, 7), 16),
  });

  const getRelativeLuminance = ({ red, green, blue }: { red: number; green: number; blue: number }) =>
    (0.2126 * getChannelLuminance(red))
    + (0.7152 * getChannelLuminance(green))
    + (0.0722 * getChannelLuminance(blue));

  const left = getRelativeLuminance(toRgb(normalizedLeft));
  const right = getRelativeLuminance(toRgb(normalizedRight));
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);

  return (lighter + 0.05) / (darker + 0.05);
};

const parseDelimitedLine = (line: string, delimiter: '\t' | ',') => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const nextCharacter = line[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        current += '"';
        index += 1;
        continue;
      }

      inQuotes = !inQuotes;
      continue;
    }

    if (character === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += character;
  }

  values.push(current.trim());
  return values;
};

const aliasMatchesField = (header: string, field: ImportFieldDefinition) => {
  const normalizedHeader = normalizeKey(header);
  if (!normalizedHeader) {
    return false;
  }

  return [field.label, ...field.aliases].some((alias) => normalizeKey(alias) === normalizedHeader);
};

const inferMappingForHeader = (header: string, fields: ImportFieldDefinition[]) => {
  const matchedField = fields.find((field) => aliasMatchesField(header, field));
  return matchedField?.key ?? IMPORT_IGNORE_FIELD;
};

const getReferenceSet = (rows: ManagementTableRow[], columnKey: string) =>
  new Set(
    rows
      .map((row) => row.cells[columnKey] ?? '')
      .map((value) => normalizeKey(value))
      .filter(Boolean)
  );

const buildStatusValidator = (allowedValues: string[]) => {
  const allowed = new Set(allowedValues.map((value) => normalizeKey(value)));

  return (value: string) => {
    if (!value.trim()) {
      return null;
    }

    return allowed.has(normalizeKey(value))
      ? null
      : `Use one of: ${allowedValues.join(', ')}.`;
  };
};

const buildSchedulerAdminAssignmentSignature = (cells: Record<string, string>) => (
  [
    normalizeKey(cells.email ?? ''),
    normalizeKey(cells.scheduleType ?? ''),
    normalizeKey(cells.serviceLine ?? ''),
    normalizeKey(cells.locationScope ?? ''),
    normalizeKey(cells.teamScope ?? ''),
  ].join('|')
);

const sectionDefinitions: Record<ManagementTableSectionId, ImportSectionDefinition> = {
  locations: {
    title: 'Hospitals / Locations',
    primaryFieldKey: 'name',
    defaultStatus: 'active',
    acceptedFormats: ['Spreadsheet paste with headers', 'Spreadsheet rows without headers', 'CSV-like rows', 'Simple newline list'],
    generalRules: [
      'Keep one location per row.',
      'Include headers when possible for the best auto-mapping.',
      'Location name is required and should be unique.',
      'Code is optional but must be unique if provided.',
      'Color must use hex format such as #0d6efd.',
    ],
    fields: [
      { key: 'name', label: 'Location Name', aliases: ['name', 'location', 'hospital', 'hospital name', 'site', 'site name'], required: true, inputPlaceholder: "St. Luke's Main Hospital" },
      { key: 'code', label: 'Code', aliases: ['abbreviation', 'abbr', 'site code'], inputPlaceholder: 'MAIN' },
      { key: 'region', label: 'Region', aliases: ['address', 'address/region', 'city', 'state', 'region'], inputPlaceholder: 'Bethlehem, PA' },
      { key: 'serviceLines', label: 'Service Lines', aliases: ['service line', 'service lines', 'lines'], inputPlaceholder: 'Trauma; Acute Care Surgery' },
      { key: 'color', label: 'Color', aliases: ['hex', 'color hex'], inputPlaceholder: '#0d6efd' },
      { key: 'status', label: 'Status', aliases: ['state'], defaultValue: 'active', inputPlaceholder: 'active' },
      { key: 'notes', label: 'Notes', aliases: ['note'], templateOnly: true, inputPlaceholder: 'Primary trauma site' },
    ],
    templateRows: [
      { name: "St. Luke's Main Hospital", code: 'MAIN', region: 'Bethlehem, PA', serviceLines: 'Trauma; Acute Care Surgery', color: '#0d6efd', status: 'active', notes: 'Primary trauma site' },
      { name: "St. Luke's East", code: 'EAST', region: 'Easton, PA', serviceLines: 'Trauma; General Surgery', color: '#198754', status: 'active', notes: '' },
    ],
    samplePaste: "Location Name\tCode\tRegion\tService Lines\tColor\tStatus\nSt. Luke's Main Hospital\tMAIN\tBethlehem, PA\tTrauma; Acute Care Surgery\t#0d6efd\tactive\nSt. Luke's West\tWEST\t\tICU / Critical Care\t#fd7e14\tdraft",
    followUpNote: 'File upload remains a follow-up; this pass focuses on paste, preview, validation, and saving into the current draft.',
  },
  'schedule-types': {
    title: 'Schedule Types',
    primaryFieldKey: 'name',
    defaultStatus: 'active',
    acceptedFormats: ['Spreadsheet paste with headers', 'Spreadsheet rows without headers', 'CSV-like rows', 'Simple newline list'],
    generalRules: [
      'Name is required and should be unique.',
      'Code is recommended and should be unique if provided.',
      'Description and service line coverage are optional guidance fields.',
      'Color must use hex format if supplied.',
    ],
    fields: [
      { key: 'name', label: 'Schedule Type Name', aliases: ['name', 'schedule type', 'type'], required: true, inputPlaceholder: 'Clinician' },
      { key: 'code', label: 'Code', aliases: ['abbreviation', 'abbr'], inputPlaceholder: 'CLIN' },
      { key: 'description', label: 'Description', aliases: ['details'], inputPlaceholder: 'Physician and APP schedule families.' },
      { key: 'serviceLineCoverage', label: 'Service Line Coverage', aliases: ['service lines', 'coverage'], inputPlaceholder: 'Trauma; General Surgery' },
      { key: 'color', label: 'Color', aliases: ['hex', 'color hex'], inputPlaceholder: '#6610f2' },
      { key: 'status', label: 'Status', aliases: ['state'], defaultValue: 'active', inputPlaceholder: 'active' },
    ],
    templateRows: [
      { name: 'Clinician', code: 'CLIN', description: 'Physician and APP schedule families.', serviceLineCoverage: 'Trauma; Acute Care Surgery; General Surgery', color: '#6610f2', status: 'active' },
      { name: 'Nursing', code: 'RN', description: 'Nursing staffing schedule families.', serviceLineCoverage: 'ICU Nursing', color: '#198754', status: 'active' },
    ],
    samplePaste: "Schedule Type Name\tCode\tDescription\tService Line Coverage\tColor\tStatus\nClinician\tCLIN\tPhysician and APP schedule families.\tTrauma; Acute Care Surgery; General Surgery\t#6610f2\tactive\nAncillary\tANC\tRespiratory therapy and support services.\tRespiratory Therapy\t#0dcaf0\tdraft",
    followUpNote: 'This supports draft seeding and validation before the schedule hierarchy is persisted elsewhere.',
  },
  'service-lines': {
    title: 'Service Lines',
    primaryFieldKey: 'name',
    defaultStatus: 'active',
    acceptedFormats: ['Spreadsheet paste with headers', 'Spreadsheet rows without headers', 'CSV-like rows', 'Simple newline list'],
    generalRules: [
      'Service line name is required and should be unique.',
      'Schedule Type is required.',
      'Locations should be separated with semicolons.',
      'Leave Locations blank only when Companywide is enabled.',
      'Color must use hex format if supplied.',
    ],
    fields: [
      { key: 'name', label: 'Service Line Name', aliases: ['name', 'service line', 'line'], required: true, inputPlaceholder: 'Trauma' },
      { key: 'code', label: 'Code', aliases: ['abbreviation', 'abbr'], inputPlaceholder: 'TRAUMA' },
      { key: 'scheduleType', label: 'Schedule Type', aliases: ['schedule type', 'type'], required: true, inputPlaceholder: 'Clinician' },
      { key: 'locations', label: 'Locations', aliases: ['location', 'location scope', 'sites'], inputPlaceholder: "St. Luke's Main Hospital; St. Luke's East" },
      { key: 'companywide', label: 'Companywide', aliases: ['company wide', 'all locations'], inputPlaceholder: 'yes' },
      { key: 'schedulerAdmin', label: 'Scheduler Admin', aliases: ['scheduler admin assignment', 'admin'], inputPlaceholder: 'Dr. Jane Smith' },
      { key: 'color', label: 'Color', aliases: ['hex', 'color hex'], inputPlaceholder: '#dc3545' },
      { key: 'status', label: 'Status', aliases: ['state'], defaultValue: 'active', inputPlaceholder: 'active' },
    ],
    templateRows: [
      { name: 'Trauma', code: 'TRAUMA', scheduleType: 'Clinician', locations: "St. Luke's Main Hospital; St. Luke's East", companywide: 'no', schedulerAdmin: 'Dr. Jane Smith', color: '#dc3545', status: 'active' },
      { name: 'Acute Care Surgery', code: 'ACS', scheduleType: 'Clinician', locations: "St. Luke's Main Hospital", companywide: 'no', schedulerAdmin: 'Dr. Alex Patel', color: '#fd7e14', status: 'active' },
    ],
    samplePaste: "Service Line Name\tCode\tSchedule Type\tLocations\tCompanywide\tScheduler Admin\tColor\tStatus\nTrauma\tTRAUMA\tClinician\tSt. Luke's Main Hospital; St. Luke's East\tno\tDr. Jane Smith\t#dc3545\tactive\nGeneral Surgery\tGS\tClinician\t\tyes\t\t#20c997\tdraft",
    followUpNote: 'Unknown schedule-type or location references are validated against the current draft tables in this browser session.',
  },
  'groups-teams': {
    title: 'Departments',
    primaryFieldKey: 'name',
    defaultStatus: 'active',
    acceptedFormats: ['Spreadsheet paste with headers', 'Spreadsheet rows without headers', 'CSV-like rows', 'Simple newline list'],
    generalRules: [
      'Department name is required and should be unique.',
      'Parent service line is required.',
      'Location and Scheduler Admin scope are optional but validated when supplied.',
      'Color must use hex format if supplied.',
    ],
    fields: [
      { key: 'name', label: 'Department Name', aliases: ['team name', 'name', 'department/team', 'group/team'], required: true, inputPlaceholder: 'Trauma Attendings' },
      { key: 'serviceLine', label: 'Service Line', aliases: ['parent service line', 'service line scope'], required: true, inputPlaceholder: 'Trauma' },
      { key: 'location', label: 'Location', aliases: ['location scope', 'site'], inputPlaceholder: "St. Luke's Main Hospital" },
      { key: 'schedulerAdmin', label: 'Scheduler Admin', aliases: ['admin'], inputPlaceholder: 'Dr. Jane Smith' },
      { key: 'color', label: 'Color', aliases: ['hex', 'color hex'], inputPlaceholder: '#6f42c1' },
      { key: 'status', label: 'Status', aliases: ['state'], defaultValue: 'active', inputPlaceholder: 'active' },
      { key: 'notes', label: 'Notes', aliases: ['note'], templateOnly: true, inputPlaceholder: 'Night coverage scope' },
    ],
    templateRows: [
      { name: 'Trauma Attendings', serviceLine: 'Trauma', location: "St. Luke's Main Hospital", schedulerAdmin: 'Dr. Jane Smith', color: '#6f42c1', status: 'active', notes: '' },
      { name: 'Night Coverage Team', serviceLine: 'Acute Care Surgery', location: "St. Luke's Main Hospital", schedulerAdmin: 'Dr. Alex Patel', color: '#0dcaf0', status: 'active', notes: '' },
    ],
    samplePaste: "Department Name\tService Line\tLocation\tScheduler Admin\tColor\tStatus\nTrauma Attendings\tTrauma\tSt. Luke's Main Hospital\tDr. Jane Smith\t#6f42c1\tactive\nNight Coverage Team\tAcute Care Surgery\tSt. Luke's Main Hospital\tDr. Alex Patel\t#0dcaf0\tactive",
    followUpNote: 'Teams remain optional overall, but pasted team rows now follow the same preview and validation workflow as required sections.',
  },
  'scheduler-admins': {
    title: 'Scheduler Admin Assignments',
    primaryFieldKey: 'email',
    defaultStatus: 'pending',
    acceptedFormats: ['Spreadsheet paste with headers', 'Spreadsheet rows without headers', 'CSV-like rows'],
    generalRules: [
      'Name, Email, Schedule Type, and Service Line are required.',
      'Email must be valid.',
      'Existing emails should link to the existing user instead of creating a duplicate user record.',
      'Location and team scope are optional but validated when supplied.',
      'Status defaults to pending so the Company Admin can review before invite send or link-save.',
    ],
    fields: [
      { key: 'name', label: 'Name', aliases: ['admin name', 'scheduler admin'], required: true, inputPlaceholder: 'Dr. Jane Smith' },
      { key: 'email', label: 'Email', aliases: ['email address'], required: true, inputPlaceholder: 'jane.smith@stlukes.org' },
      { key: 'scheduleType', label: 'Schedule Type', aliases: ['schedule type', 'type'], required: true, inputPlaceholder: 'Clinician' },
      { key: 'serviceLine', label: 'Service Line', aliases: ['service line scope', 'line'], required: true, inputPlaceholder: 'Trauma' },
      { key: 'locationScope', label: 'Location Scope', aliases: ['locations', 'location', 'site'], inputPlaceholder: "St. Luke's Main Hospital; St. Luke's East" },
      { key: 'teamScope', label: 'Department Scope', aliases: ['department/team', 'group/team', 'team', 'group'], inputPlaceholder: 'Trauma Attendings' },
      { key: 'inviteStatus', label: 'Invite Status', aliases: ['invite', 'invite state'], defaultValue: 'Invite pending review', inputPlaceholder: 'Invite sent' },
      { key: 'status', label: 'Status', aliases: ['state'], defaultValue: 'pending', inputPlaceholder: 'pending' },
      { key: 'notes', label: 'Notes', aliases: ['note'], templateOnly: true, inputPlaceholder: 'Trauma schedule lead' },
    ],
    templateRows: [
      { name: 'Dr. Jane Smith', email: 'jane.smith@stlukes.org', scheduleType: 'Clinician', serviceLine: 'Trauma', locationScope: "St. Luke's Main Hospital; St. Luke's East", teamScope: 'Trauma Attendings; Trauma Residents/APPs', inviteStatus: 'Invite sent', status: 'pending', notes: 'Trauma schedule lead' },
      { name: 'Dr. Alex Patel', email: 'alex.patel@stlukes.org', scheduleType: 'Clinician', serviceLine: 'Acute Care Surgery', locationScope: "St. Luke's Main Hospital", teamScope: 'All', inviteStatus: 'Activated', status: 'active', notes: '' },
    ],
    samplePaste: "Name\tEmail\tSchedule Type\tService Line\tLocation Scope\tTeam Scope\tInvite Status\tStatus\nDr. Jane Smith\tjane.smith@stlukes.org\tClinician\tTrauma\tSt. Luke's Main Hospital; St. Luke's East\tTrauma Attendings\tInvite sent\tpending\nDr. Alex Patel\talex.patel@stlukes.org\tClinician\tAcute Care Surgery\tSt. Luke's Main Hospital\tAll\tActivated\tactive",
    followUpNote: 'Invite delivery and acceptance automation stay in CAR-111, but assignment rows now validate before they enter the current draft table.',
  },
};

const statusValidators: Record<ManagementTableSectionId, (value: string) => string | null> = {
  locations: buildStatusValidator(['active', 'inactive', 'draft', 'archived']),
  'schedule-types': buildStatusValidator(['active', 'inactive', 'draft']),
  'service-lines': buildStatusValidator(['active', 'inactive', 'draft']),
  'groups-teams': buildStatusValidator(['active', 'inactive', 'draft']),
  'scheduler-admins': buildStatusValidator(['pending', 'active', 'deactivated', 'expired']),
};

const colorHexRegex = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getImportSectionDefinition = (sectionId: ManagementTableSectionId) => sectionDefinitions[sectionId];

export const buildTemplateCsv = (sectionId: ManagementTableSectionId) => {
  const definition = sectionDefinitions[sectionId];
  const fields = definition.fields.filter((field) => !field.templateOnly);
  const headers = fields.map((field) => field.label);
  const rows = definition.templateRows.map((row) =>
    fields.map((field) => {
      const value = row[field.key] ?? '';
      return value.includes(',') || value.includes('"')
        ? `"${value.replaceAll('"', '""')}"`
        : value;
    })
  );

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
};

export const parseImportInput = (sectionId: ManagementTableSectionId, input: string): ParsedImportBatch | null => {
  const definition = sectionDefinitions[sectionId];
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return null;
  }

  const delimiter: ParsedImportBatch['delimiter'] = lines.some((line) => line.includes('\t'))
    ? 'tab'
    : lines.some((line) => line.includes(','))
      ? 'csv'
      : 'newline';

  const parsedRows = lines.map((line) => {
    if (delimiter === 'tab') {
      return parseDelimitedLine(line, '\t');
    }

    if (delimiter === 'csv') {
      return parseDelimitedLine(line, ',');
    }

    return [line];
  });

  const headerMatches = parsedRows[0].filter((value) =>
    definition.fields.some((field) => aliasMatchesField(value, field))
  ).length;
  const headerDetected = parsedRows[0].length > 1 && headerMatches >= Math.max(1, Math.ceil(parsedRows[0].length / 2));
  const simpleList = parsedRows.every((row) => row.length === 1) && !headerDetected;
  const fieldsInSequentialOrder = definition.fields.filter((field) => !field.templateOnly);

  if (simpleList) {
    return {
      sourceMode: 'simple-list',
      delimiter,
      columnLabels: [definition.fields.find((field) => field.key === definition.primaryFieldKey)?.label ?? 'Name'],
      records: parsedRows,
      defaultMappings: [definition.primaryFieldKey],
      summary: `Parsed ${parsedRows.length} newline row${parsedRows.length === 1 ? '' : 's'} into the primary ${definition.title.toLowerCase()} field.`,
    };
  }

  const columnCount = Math.max(...parsedRows.map((row) => row.length));
  const columnLabels = headerDetected
    ? Array.from({ length: columnCount }, (_, index) => parsedRows[0][index] || `Column ${index + 1}`)
    : Array.from({ length: columnCount }, (_, index) => `Column ${index + 1}`);
  const records = headerDetected ? parsedRows.slice(1) : parsedRows;
  const defaultMappings = headerDetected
    ? columnLabels.map((label) => inferMappingForHeader(label, definition.fields))
    : columnLabels.map((_, index) => fieldsInSequentialOrder[index]?.key ?? IMPORT_IGNORE_FIELD);

  return {
    sourceMode: headerDetected ? 'headered' : 'headerless',
    delimiter,
    columnLabels,
    records,
    defaultMappings,
    summary: headerDetected
      ? `Detected ${columnCount} incoming column${columnCount === 1 ? '' : 's'} with a header row and ${records.length} data row${records.length === 1 ? '' : 's'}.`
      : `Parsed ${records.length} row${records.length === 1 ? '' : 's'} without headers. Review the column mapping before commit.`,
  };
};

export const buildImportPreview = (
  sectionId: ManagementTableSectionId,
  batch: ParsedImportBatch,
  mappings: string[],
  overrides: Record<number, Record<string, string>>,
  currentRows: ManagementTableRow[],
  store: ManagementTableStore
): ImportPreview => {
  const definition = sectionDefinitions[sectionId];
  const counts: Record<ImportFieldStatus, number> = {
    valid: 0,
    warning: 0,
    error: 0,
  };

  const mappingWarnings = definition.fields
    .filter((field) => field.required)
    .filter((field) => !mappings.includes(field.key))
    .map((field) => `${field.label} is not mapped to any incoming column.`);

  const existingNames = getReferenceSet(currentRows, 'name');
  const existingCodes = getReferenceSet(currentRows, 'code');
  const existingEmails = getReferenceSet(currentRows, 'email');
  const existingSchedulerAdminAssignments = new Set(
    currentRows.map((row) => buildSchedulerAdminAssignmentSignature(row.cells))
  );
  const knownLocations = getReferenceSet(store.locations, 'name');
  const knownScheduleTypes = getReferenceSet(store['schedule-types'], 'name');
  const knownServiceLines = getReferenceSet(store['service-lines'], 'name');
  const knownTeams = getReferenceSet(store['groups-teams'], 'name');
  const knownSchedulerAdmins = getReferenceSet(store['scheduler-admins'], 'name');
  const statusValidator = statusValidators[sectionId];
  const duplicateBatchCounts = definition.fields.reduce<Record<string, Record<string, number>>>((accumulator, field) => {
    accumulator[field.key] = {};
    return accumulator;
  }, {});

  const draftRows = batch.records.map((record, rowIndex) => {
    const draft = definition.fields.reduce<Record<string, string>>((accumulator, field) => {
      accumulator[field.key] = field.defaultValue ?? '';
      return accumulator;
    }, {});

    mappings.forEach((fieldKey, columnIndex) => {
      if (!fieldKey || fieldKey === IMPORT_IGNORE_FIELD) {
        return;
      }

      draft[fieldKey] = record[columnIndex]?.trim() ?? draft[fieldKey];
    });

    const rowOverrides = overrides[rowIndex] ?? {};
    Object.entries(rowOverrides).forEach(([fieldKey, value]) => {
      draft[fieldKey] = value;
    });

    definition.fields.forEach((field) => {
      if (!draft[field.key].trim() && field.defaultValue) {
        draft[field.key] = field.defaultValue;
      }
    });

    return draft;
  });

  draftRows.forEach((draft) => {
    ['name', 'code', 'email'].forEach((fieldKey) => {
      const normalizedValue = normalizeKey(draft[fieldKey] ?? '');
      if (!normalizedValue) {
        return;
      }

      duplicateBatchCounts[fieldKey][normalizedValue] = (duplicateBatchCounts[fieldKey][normalizedValue] ?? 0) + 1;
    });
  });

  const schedulerAdminBatchAssignmentCounts: Record<string, number> = {};
  if (sectionId === 'scheduler-admins') {
    draftRows.forEach((draft) => {
      const signature = buildSchedulerAdminAssignmentSignature(draft);
      if (!signature.replace(/\|/g, '').trim()) {
        return;
      }

      schedulerAdminBatchAssignmentCounts[signature] = (schedulerAdminBatchAssignmentCounts[signature] ?? 0) + 1;
    });
  }

  const rows = draftRows.map<ImportPreviewRow>((draft, rowIndex) => {
    const cellValidation = definition.fields.reduce<Record<string, ImportCellValidation>>((accumulator, field) => {
      accumulator[field.key] = {
        status: 'valid',
        messages: [],
      };
      return accumulator;
    }, {});

    const rowMessages: Array<{ severity: ImportFieldStatus; message: string }> = [];

    const addMessage = (fieldKey: string | null, severity: ImportFieldStatus, message: string) => {
      if (fieldKey) {
        const currentValidation = cellValidation[fieldKey];
        if (!currentValidation) {
          return;
        }

        currentValidation.status = currentValidation.status === 'error' || severity === 'error'
          ? 'error'
          : currentValidation.status === 'warning' || severity === 'warning'
            ? 'warning'
            : 'valid';
        currentValidation.messages.push(message);
      }

      rowMessages.push({ severity, message });
    };

    definition.fields.forEach((field) => {
      const value = draft[field.key] ?? '';

      if (field.required && !value.trim()) {
        addMessage(field.key, 'error', `${field.label} is required.`);
      }
    });

    if (draft.status.trim()) {
      const statusError = statusValidator(draft.status);
      if (statusError) {
        addMessage('status', 'error', statusError);
      }
    }

    if (draft.color.trim() && !colorHexRegex.test(draft.color.trim())) {
      addMessage('color', 'error', 'Use a valid color hex such as #0d6efd.');
    } else if (draft.color.trim()) {
      const normalizedColor = expandHexColor(draft.color.trim());
      if (normalizedColor) {
        draft.color = normalizedColor;
        const strongestContrast = Math.max(
          getContrastRatio(normalizedColor, '#FFFFFF'),
          getContrastRatio(normalizedColor, '#111827')
        );

        if (strongestContrast < 4.5) {
          addMessage('color', 'warning', 'Low contrast warning: choose a lighter or darker color so labels remain readable.');
        }
      }
    }

    if (draft.name.trim()) {
      const normalizedName = normalizeKey(draft.name);
      if (existingNames.has(normalizedName)) {
        addMessage('name', 'error', 'Name duplicates an existing draft row in this section.');
      }
      if ((duplicateBatchCounts.name[normalizedName] ?? 0) > 1) {
        addMessage('name', 'error', 'Name appears more than once in this import batch.');
      }
    }

    if (draft.code.trim()) {
      const normalizedCode = normalizeKey(draft.code);
      if (existingCodes.has(normalizedCode)) {
        addMessage('code', 'error', 'Code duplicates an existing draft row in this section.');
      }
      if ((duplicateBatchCounts.code[normalizedCode] ?? 0) > 1) {
        addMessage('code', 'error', 'Code appears more than once in this import batch.');
      }
    }

    if (sectionId === 'locations') {
      if (!draft.region.trim()) {
        addMessage('region', 'warning', 'Region or address is blank and should be reviewed before sign-off.');
      }

      splitMultiValueCell(draft.serviceLines).forEach((serviceLine) => {
        if (!knownServiceLines.has(normalizeKey(serviceLine))) {
          addMessage('serviceLines', 'warning', `Service line reference "${serviceLine}" is not in the current draft list.`);
        }
      });
    }

    if (sectionId === 'schedule-types' && !draft.description.trim()) {
      addMessage('description', 'warning', 'Description is blank. Add context before finalizing the hierarchy.');
    }

    if (sectionId === 'service-lines') {
      const companywide = isTruthyValue(draft.companywide);
      const locations = splitMultiValueCell(draft.locations);

      if (!draft.scheduleType.trim()) {
        addMessage('scheduleType', 'error', 'Schedule Type is required.');
      } else if (!knownScheduleTypes.has(normalizeKey(draft.scheduleType))) {
        addMessage('scheduleType', 'error', `Schedule Type "${draft.scheduleType}" is not available in the current draft list.`);
      }

      if (!companywide && locations.length === 0) {
        addMessage('locations', 'error', 'Provide at least one location or set Companywide to yes.');
      }

      locations.forEach((location) => {
        if (!knownLocations.has(normalizeKey(location))) {
          addMessage('locations', 'error', `Location "${location}" is not available in the current draft list.`);
        }
      });

      if (!draft.schedulerAdmin.trim()) {
        addMessage('schedulerAdmin', 'warning', 'Scheduler Admin coverage is still blank for this service line.');
      } else if (!knownSchedulerAdmins.has(normalizeKey(draft.schedulerAdmin))) {
        addMessage('schedulerAdmin', 'warning', `Scheduler Admin "${draft.schedulerAdmin}" is not yet in the current assignments draft list.`);
      }
    }

    if (sectionId === 'groups-teams') {
      if (!draft.serviceLine.trim()) {
        addMessage('serviceLine', 'error', 'Parent service line is required.');
      } else if (!knownServiceLines.has(normalizeKey(draft.serviceLine))) {
        addMessage('serviceLine', 'error', `Service line "${draft.serviceLine}" is not available in the current draft list.`);
      }

      if (draft.location.trim() && !knownLocations.has(normalizeKey(draft.location))) {
        addMessage('location', 'error', `Location "${draft.location}" is not available in the current draft list.`);
      }

      if (draft.schedulerAdmin.trim() && !knownSchedulerAdmins.has(normalizeKey(draft.schedulerAdmin))) {
        addMessage('schedulerAdmin', 'warning', `Scheduler Admin "${draft.schedulerAdmin}" is not yet in the current assignments draft list.`);
      }
    }

    if (sectionId === 'scheduler-admins') {
      const normalizedEmail = normalizeKey(draft.email);
      const assignmentSignature = buildSchedulerAdminAssignmentSignature(draft);
      if (!draft.email.trim()) {
        addMessage('email', 'error', 'Email is required.');
      } else if (!emailRegex.test(draft.email.trim())) {
        addMessage('email', 'error', 'Use a valid email address.');
      } else {
        if (existingEmails.has(normalizedEmail)) {
          addMessage('email', 'warning', 'Email already exists in the current Scheduler Admin draft and should link to that user record.');
        }
        if ((duplicateBatchCounts.email[normalizedEmail] ?? 0) > 1) {
          addMessage('email', 'warning', 'Email appears more than once in this import batch. Review whether the same user is intentionally covering multiple scopes.');
        }
      }

      if (!draft.scheduleType.trim()) {
        addMessage('scheduleType', 'error', 'Schedule Type scope is required.');
      } else if (!knownScheduleTypes.has(normalizeKey(draft.scheduleType))) {
        addMessage('scheduleType', 'error', `Schedule Type "${draft.scheduleType}" is not available in the current draft list.`);
      }

      if (!draft.serviceLine.trim()) {
        addMessage('serviceLine', 'error', 'Service line scope is required.');
      } else if (!knownServiceLines.has(normalizeKey(draft.serviceLine))) {
        addMessage('serviceLine', 'error', `Service line "${draft.serviceLine}" is not available in the current draft list.`);
      }

      splitMultiValueCell(draft.locationScope).forEach((location) => {
        if (!knownLocations.has(normalizeKey(location))) {
          addMessage('locationScope', 'error', `Location "${location}" is not available in the current draft list.`);
        }
      });

      splitMultiValueCell(draft.teamScope)
        .filter((value) => normalizeKey(value) !== 'all')
        .forEach((team) => {
          if (!knownTeams.has(normalizeKey(team))) {
            addMessage('teamScope', 'error', `Team "${team}" is not available in the current draft list.`);
          }
        });

      if (existingSchedulerAdminAssignments.has(assignmentSignature)) {
        addMessage('serviceLine', 'error', 'This exact email + scope assignment already exists in the current draft table.');
      }

      if ((schedulerAdminBatchAssignmentCounts[assignmentSignature] ?? 0) > 1) {
        addMessage('serviceLine', 'error', 'This exact email + scope assignment appears more than once in this import batch.');
      }
    }

    const rowStatus = rowMessages.some((message) => message.severity === 'error')
      ? 'error'
      : rowMessages.some((message) => message.severity === 'warning')
        ? 'warning'
        : 'valid';

    counts[rowStatus] += 1;

    return {
      rowIndex,
      status: rowStatus,
      messages: rowMessages.map((message) => message.message),
      cells: draft,
      cellValidation,
    };
  });

  return {
    rows,
    counts,
    committableCount: rows.filter((row) => row.status !== 'error').length,
    mappingWarnings,
  };
};
